import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "@/api/admin.api";
import { formatDay } from "./first-round";
import { type Column, DataTable } from "./ui/DataTable";
import { PageHeader, useDebouncedValue } from "./ui/PageHeader";
import { SearchBox } from "./ui/SearchBox";

const isSolo = (company?: string | null) => !!company?.startsWith("individual_");

const columns: Column<any>[] = [
  { key: "email", header: "Email", sortValue: (r) => r.email, cell: (r) => <span className="font-medium">{r.email}</span> },
  { key: "name", header: "Name", sortValue: (r) => r.name, cell: (r) => r.name || <span className="text-slate-400">—</span> },
  {
    key: "company",
    header: "Company",
    sortValue: (r) => (isSolo(r.company) ? "" : r.company),
    cell: (r) =>
      isSolo(r.company) ? (
        <span className="italic text-slate-400">Solo recruiter</span>
      ) : (
        r.company || <span className="text-slate-400">—</span>
      ),
  },
  { key: "created", header: "Joined", sortValue: (r) => new Date(r.createdAt), cell: (r) => formatDay(r.createdAt) },
  { key: "id", header: "ID", cell: (r) => <code className="font-mono text-xs text-slate-500">{r.id}</code> },
];

export function RecruitersManagement() {
  const [query, setQuery] = useState("");
  const search = useDebouncedValue(query.trim());
  const { data: recruiters, isFetching, error } = useQuery({
    queryKey: ["admin-recruiters", search],
    queryFn: () => adminApi.searchRecruiters(search),
    // Keep the last results on screen while the next search runs.
    placeholderData: (previous) => previous,
  });
  const navigate = useNavigate();

  return (
    <div className="max-w-6xl">
      <PageHeader title="Recruiters" description="Search by email, name or company. Open a recruiter for their organisations, plan, usage and support actions." />
      <SearchBox value={query} onChange={setQuery} placeholder="Search recruiters" busy={isFetching} />
      <DataTable
        label="Recruiters"
        columns={columns}
        rows={recruiters}
        rowKey={(r) => r.id}
        isLoading={!recruiters && isFetching}
        error={error}
        emptyMessage={search ? `No recruiters match "${search}".` : "No recruiters yet."}
        onRowClick={(r) => navigate(`/admin/recruiters/${r.id}`)}
      />
    </div>
  );
}
